import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useRef, useState } from "react";
import { useAppServices } from "../../../hook/services";
import ReactQuill from "react-quill";
import { useSocket } from "../../../context/socket";
import "react-quill/dist/quill.snow.css";
import Editor from "../../../components/editor";

const TicketLayer = () => {
  const socket = useSocket();
  const AppService = useAppServices();
  const [processing, setProcessing] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
  });

  const messagesEndRef = useRef(null);

  // -------------------------
  // Get tickets & messages
  // -------------------------
  const getTickets = async () => {
    const { response } = await AppService.support.get_tickets_with_messages();
    if (response) {
      setTickets(response.data);
      setMessages(response.messages);
      setProcessing(false);
    } else {
      setTickets([]);
      setMessages([]);
      setProcessing(false);
    }
  };

  const onLoad = () => {
    getTickets();
  };

  useEffect(() => {
    onLoad();
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, activeTicket]);

  // -------------------------
  // SOCKET EVENTS
  // -------------------------
  useEffect(() => {
    socket?.on("messageResponse", (data) => {
      setMessages((prev) => [...prev, data]);
    });

    socket?.on("ticketStatusUpdated", ({ _id, status }) => {
      setTickets((prev) =>
        prev.map((t) => (t._id === _id ? { ...t, status } : t))
      );
      if (activeTicket?._id === _id) {
        setActiveTicket((prev) => ({ ...prev, status }));
      }
    });

    return () => {
      socket?.off("messageResponse");
      socket?.off("ticketStatusUpdated");
    };
  }, [activeTicket]);

  const statusBadge = (status) => {
    switch (status) {
      case "open":
        return "bg-success";
      case "pending":
        return "bg-warning text-dark";
      case "closed":
        return "bg-secondary";
      default:
        return "bg-light text-dark";
    }
  };

  // -------------------------
  // Update Ticket Status
  // -------------------------
  const updateTicketStatus = (ticketId, newStatus) => {
    socket?.emit("updateTicketStatus", {
      _id: ticketId,
      status: newStatus,
    });
  };

  // -------------------------
  // Send Message
  // -------------------------
  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeTicket) return;
    socket?.emit("sendMessage", {
      ticket_id: activeTicket.ticket_id,
      ticketRefId: activeTicket._id,
      sender: "Support",
      text: newMessage,
      id: `${socket.id}${Math.random()}`,
      socketID: socket.id,
    });

    setNewMessage("");
  };

  // -------------------------
  // Filters
  // -------------------------
  const filteredTickets = tickets.filter((t) => {
    const matchesSearch =
      t.ticket_id?.toLowerCase().includes(filters.search.toLowerCase()) ||
      t.subject?.toLowerCase().includes(filters.search.toLowerCase());
    const matchesStatus =
      filters.status === "all" || t.status === filters.status;
    return matchesSearch && matchesStatus;
  });

  const ticketMessages = activeTicket
    ? messages.filter((msg) => msg.ticket_id === activeTicket.ticket_id)
    : [];

  if (processing) {
    return (
      <div className="min-vh-100" style={{ backgroundColor: "#F5F6FA" }}>
        <div className="container-fluid p-4">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <div className="text-center">
              <div
                className="spinner-border text-primary mb-3"
                role="status"
                style={{ width: "3rem", height: "3rem" }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted">Loading tickets...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-wrapper">
      {/* Sidebar - Ticket List */}
      {/* Sidebar - Ticket List */}
      <div
        className="chat-sidebar card position-sticky top-0"
        style={{
          maxHeight: "88vh",
          overflowY: "auto",
        }}
      >
        {/* Filters */}
        <div className="p-3 border-bottom bg-white sticky-top">
          <div className="chat-search mb-2">
            <span className="icon">
              <Icon icon="iconoir:search" />
            </span>
            <input
              type="text"
              placeholder="Search tickets..."
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
            />
          </div>
          <select
            className="form-select form-select-sm"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="pending">Pending</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        {/* Ticket List */}
        <div className="chat-all-list">
          {filteredTickets.length === 0 ? (
            <div className="p-3 text-center text-muted">No tickets found</div>
          ) : (
            filteredTickets.map((ticket) => (
              <div
                key={ticket._id}
                className={`chat-sidebar-single ${
                  activeTicket?._id === ticket._id
                    ? "active bg-primary-light"
                    : ""
                } ${ticket.status === "closed" ? "opacity-75" : ""}`}
                style={{ cursor: "pointer", flexWrap: "unset" }}
                onClick={() => {
                  setActiveTicket(ticket);
                  if (ticket.status === "pending") {
                    updateTicketStatus(ticket._id, "open");
                  }
                }}
              >
                <img
                  src={ticket.appRefId?.appImage}
                  alt={ticket.appRefId?.appName}
                  className="me-2 rounded"
                  style={{ width: "50px", height: "50px", objectFit: "cover" }}
                />
                <div className="flex-grow-1">
                  <div className="fw-semibold small">
                    {ticket.appRefId?.appName}
                  </div>
                  <b style={{ fontSize: "10px" }}>#{ticket.ticket_id}</b>
                  <div className="text-muted small">{ticket.subject}</div>
                </div>
                <span className={`badge ${statusBadge(ticket.status)}`}>
                  {ticket.status}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Ticket Conversation */}
      <div className="chat-main card">
        {!activeTicket ? (
          <div className="d-flex justify-content-center align-items-center h-100">
            <p className="text-muted">Select a ticket to view conversation</p>
          </div>
        ) : (
          <>
            <div className="card-header d-flex justify-content-between">
              <div className="d-flex align-items-center">
                <img
                  src={activeTicket.appRefId?.appImage}
                  alt={activeTicket.appRefId?.appName}
                  className="me-3 rounded"
                  style={{ width: "60px", height: "60px", objectFit: "cover" }}
                />
                <div>
                  <h5 className="mb-1">{activeTicket.appRefId?.appName}</h5>
                  <p className="mb-0 text-muted small">
                    Ticket ID: <strong>{activeTicket.ticket_id}</strong>
                  </p>
                  <span
                    className={`badge ${
                      activeTicket.status === "open"
                        ? "bg-success"
                        : "bg-danger"
                    }`}
                  >
                    {activeTicket.status.toUpperCase()}
                  </span>
                </div>
              </div>
              <div>
                <select
                  className="form-select form-select-sm"
                  value={activeTicket.status}
                  onChange={(e) =>
                    updateTicketStatus(activeTicket._id, e.target.value)
                  }
                  style={{ width: "140px" }}
                >
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>
            <div className="card-body">
              {/* Ticket Info */}
              <h6 className="fw-bold">{activeTicket.subject}</h6>
              <p>{activeTicket.description}</p>
              {activeTicket.loomVideo && (
                <a
                  href={activeTicket.loomVideo}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="d-block mb-2 text-primary"
                >
                  Loom Video
                </a>
              )}
              <p className="text-muted small">
                Created at: {new Date(activeTicket.createdAt).toLocaleString()}
              </p>

              {/* Conversation */}
              <div
                className="chat-message-list p-3"
                style={{
                  maxHeight: "40vh",
                  overflowY: "auto",
                  backgroundColor: "#f9fafb",
                }}
              >
                {ticketMessages.map((msg, index) => {
                  const isUser = msg.sender === "User";
                  return (
                    <div
                      key={msg._id || index}
                      className={`d-flex mb-4 ${
                        isUser
                          ? "align-items-start"
                          : "flex-row-reverse align-items-start"
                      }`}
                    >
                      {/* Avatar */}
                      <div className="me-2 ms-2">
                        <Icon
                          icon={isUser ? "mdi:account-circle" : "mdi:headset"}
                          className={`text-4xl ${
                            isUser ? "text-primary" : "text-secondary"
                          }`}
                        />
                      </div>

                      {/* Message bubble */}
                      <div style={{ maxWidth: "70%" }}>
                        <div className="mb-1">
                          <small className="fw-bold text-muted">
                            {isUser
                              ? `${
                                  activeTicket?.userName || activeTicket?.email
                                } ${
                                  activeTicket?.userType ||
                                  activeTicket?.userRole
                                    ? `(${activeTicket?.userType} ${activeTicket?.userRole})`
                                    : ""
                                }`
                              : "You"}
                          </small>
                        </div>

                        <div
                          className={`p-3 rounded-4 shadow-sm position-relative chat-bubble ${
                            isUser
                              ? "bg-light text-dark"
                              : "bg-gradient-primary text-white"
                          }`}
                        >
                          <div dangerouslySetInnerHTML={{ __html: msg.text }} />
                        </div>

                        <div
                          className={`mt-1 ${
                            isUser ? "text-start" : "text-end"
                          }`}
                        >
                          <small className="text-muted">
                            {new Date(msg.createdAt).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </small>
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div ref={messagesEndRef}></div>
              </div>

              {/* Reply box */}
              {activeTicket.status === "closed" ? (
                <div className="p-3 border-top bg-light text-center">
                  <p className="text-muted mb-0">
                    This ticket is <strong>closed</strong>. You can only view
                    previous messages.
                  </p>
                </div>
              ) : (
                <form
                  className="chat-message-box p-3 border-top"
                  onSubmit={handleSendMessage}
                >
                  <div className="w-100">
                    <Editor
                      data={newMessage}
                      handleChange={(value) => setNewMessage(value)}
                    />
                  </div>
                  <div className="d-flex align-items-center justify-content-end w-100 mt-2">
                    <button
                      type="submit"
                      className="btn btn-primary rounded-circle shadow-sm"
                      style={{ width: "50px", height: "50px" }}
                    >
                      <Icon icon="f7:paperplane" className="text-lg" />
                    </button>
                  </div>
                </form>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TicketLayer;
